import { createContext, useContext, ReactNode } from 'react'
import { logger } from '../utils/logger'

export type ThemeName = 'default' | 'dino-casino' | 'superjackpotslots' | 'habbo' | 'merkur24' | 'generic'

interface ThemeConfig {
    name: ThemeName
    colors: {
        primary: string
        secondary: string
        accent: string
        background: string
        cardGradientFrom: string
        cardGradientTo: string
        textPrimary: string
        textSecondary: string
    }
    realAmount: {
        color: string
        textShadow: string
        fontWeight: number
    }
    shopBackground: string
}

const themes: Record<ThemeName, ThemeConfig> = {
    generic: {
        name: 'generic',
        colors: { primary: '#333c4c', secondary: '#1d2430', accent: '#e8a33c', background: '#101319', cardGradientFrom: '#333c4c', cardGradientTo: '#1d2430', textPrimary: '#f6f8fb', textSecondary: '#ced5e1' },
        realAmount: { color: '#ffffff', textShadow: '0 2px 4px #0008', fontWeight: 800 },
        shopBackground: '#101319',
    },
    default: {
        name: 'default',
        colors: {
            primary: '#7132e8',
            secondary: '#a155ff',
            accent: '#ff00a8',
            background: '#2a004a',
            cardGradientFrom: '#f1d173',
            cardGradientTo: '#e5a823',
            textPrimary: '#ffffff',
            textSecondary: 'rgba(255, 255, 255, 0.8)'
        },
        realAmount: {
            color: '#ffffff',
            textShadow: `
                5px 0 35px rgba(31, 0, 42, 0.5),
                0 0 15px rgba(255, 0, 168, 0.75),
                0 0 15px rgba(255, 0, 168, 0.75),
                0 0 15px rgba(255, 0, 168, 0.75),
                0 0 15px rgba(255, 0, 168, 0.75),
                0 4px 8px rgba(0, 0, 0, 0.8)
            `,
            fontWeight: 900
        },
        shopBackground: 'linear-gradient(135deg, #2a004a 0%, #3c2a50 30%, #2a004a 70%, #1a0030 100%)'
    },
    'dino-casino': {
        name: 'dino-casino',
        colors: {
            primary: '#00628c',
            secondary: '#0088b8',
            accent: '#00d4ff',
            background: '#001a28',
            cardGradientFrom: '#0088b8',
            cardGradientTo: '#00628c',
            textPrimary: '#ffffff',
            textSecondary: 'rgba(255, 255, 255, 0.8)'
        },
        realAmount: {
            color: '#ffffff',
            textShadow: `
                5px 0 35px rgba(0, 98, 140, 0.5),
                0 0 15px rgba(0, 98, 140, 0.75),
                0 0 15px rgba(0, 98, 140, 0.75),
                0 0 15px rgba(0, 98, 140, 0.75),
                0 0 15px rgba(0, 98, 140, 0.75),
                0 4px 8px rgba(0, 0, 0, 0.8)
            `,
            fontWeight: 900
        },
        shopBackground: 'linear-gradient(135deg, #001a28 0%, #003850 30%, #001a28 70%, #000d14 100%)'
    },
    superjackpotslots: {
        name: 'superjackpotslots',
        colors: {
            primary: '#ffd700',
            secondary: '#ffaa00',
            accent: '#ff6b00',
            background: '#1a0a00',
            cardGradientFrom: '#ffd700',
            cardGradientTo: '#ffaa00',
            textPrimary: '#ffffff',
            textSecondary: 'rgba(255, 255, 255, 0.8)'
        },
        realAmount: {
            color: '#ffffff',
            textShadow: `
                0 10px 30px rgba(0, 0, 0, 0.4),
                0 4px 8px rgba(0, 0, 0, 0.8)
            `,
            fontWeight: 700
        },
        shopBackground: 'linear-gradient(135deg, #1a0a00 0%, #3a2200 30%, #1a0a00 70%, #0d0500 100%)'
    },
    habbo: {
        name: 'habbo',
        colors: {
            primary: '#007ab8',
            secondary: '#0099dd',
            accent: '#ff6b00',
            background: '#001f3f',
            cardGradientFrom: '#0099dd',
            cardGradientTo: '#007ab8',
            textPrimary: '#ffffff',
            textSecondary: 'rgba(255, 255, 255, 0.8)'
        },
        realAmount: {
            color: '#ffffff',
            textShadow: `
                0 10px 30px rgba(0, 0, 0, 0.4),
                0 4px 8px rgba(0, 0, 0, 0.8)
            `,
            fontWeight: 700
        },
        shopBackground: 'linear-gradient(135deg, #001f3f 0%, #003d7a 30%, #001f3f 70%, #000f1f 100%)'
    },
    merkur24: {
        name: 'merkur24',
        colors: {
            primary: '#e60000',
            secondary: '#ff3333',
            accent: '#ffd700',
            background: '#1a0000',
            cardGradientFrom: '#e60000',
            cardGradientTo: '#990000',
            textPrimary: '#ffffff',
            textSecondary: 'rgba(255, 255, 255, 0.8)'
        },
        realAmount: {
            color: '#ffffff',
            textShadow: `
                5px 0 35px rgba(230, 0, 0, 0.5),
                0 0 15px rgba(255, 51, 51, 0.75),
                0 0 15px rgba(255, 51, 51, 0.75),
                0 0 15px rgba(255, 51, 51, 0.75),
                0 0 15px rgba(255, 51, 51, 0.75),
                0 4px 8px rgba(0, 0, 0, 0.8)
            `,
            fontWeight: 900
        },
        shopBackground: 'linear-gradient(135deg, #1a0000 0%, #3d0000 30%, #1a0000 70%, #0d0000 100%)'
    }
}

interface ThemeContextType {
    theme: ThemeConfig
    themeName: ThemeName
    setTheme: (themeName: ThemeName) => void
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined)

interface ThemeProviderProps {
    children: ReactNode
    initialTheme?: ThemeName
}

export function ThemeProvider({ children, initialTheme = 'default' }: ThemeProviderProps) {
    // In a real app, you might want to use useState here
    // For now, we'll use the initial theme as static
    const themeName = initialTheme
    const theme = themes[themeName]

    const setTheme = (newThemeName: ThemeName) => {
        // This would update state in a real implementation
        logger.log('COMPONENT', 'Setting theme to:', newThemeName)
    }

    return (
        <ThemeContext.Provider value={{ theme, themeName, setTheme }}>
            {children}
        </ThemeContext.Provider>
    )
}

export function useTheme(): ThemeContextType {
    const context = useContext(ThemeContext)
    if (!context) {
        throw new Error('useTheme must be used within a ThemeProvider')
    }
    return context
}

export { themes }
export type { ThemeConfig }

